// src/components/forms/TaxReliefForm.jsx
import React, { useState, useContext } from "react";
import {
  Box,
  Stack,
  HStack,
  VStack,
  Heading,
  Text,
  Input,
  Button,
  Separator,
  NativeSelect,
  Checkbox,
} from "@chakra-ui/react";
import prospectContext from "../../../context/prospect/prospectContext";
import ConsentNotice from "./ConsentNotice";

const TAX_TYPES = [
  { value: "income_1040", label: "Individual Income (1040)" },
  { value: "business_1120_1065", label: "Business (1120 / 1065)" },
  { value: "payroll_941", label: "Payroll / 941" },
  { value: "sales_use", label: "Sales / Use" },
  { value: "state", label: "State Taxes" },
  { value: "other", label: "Other" },
  { value: "not_sure", label: "Not sure" },
];

const AMOUNT_OWED = [
  "< $10,000",
  "$10,000 – $25,000",
  "$25,000 – $50,000",
  "$50,000 – $100,000",
  "> $100,000",
  "Not sure",
];

const RETURNS_NEEDED = [
  "1040 (Individual)",
  "1120 (Corp)",
  "1065 (Partnership)",
  "941 (Quarterly Payroll)",
  "940 (FUTA)",
  "State returns",
  "Other",
  "Not sure",
];

const NOTICES = [
  "CP14 (Balance Due)",
  "CP501 / CP503 (Reminder)",
  "CP504 (Intent to Levy)",
  "LT11 / Letter 1058 (Final Notice of Intent to Levy)",
  "Federal Tax Lien",
  "Wage Garnishment / Bank Levy",
  "State Notice",
  "None / Not sure",
];

const INCOME_SOURCES = [
  "W-2",
  "1099 / Self-Employed",
  "Business Owner",
  "Mixed W-2 & 1099",
  "Retired / Social Security",
  "Unemployment / Benefits",
  "Other",
  "Not sure",
];

export default function TaxReliefForm({
  adminEmail,
  companyKey = "TAG",
  source = "TaxReliefForm",
  onSuccess,
  // postUrl = "/prospects", // no longer used; handled by ProspectContext
}) {
  const { createProspect } = useContext(prospectContext);

  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    state: "",
    taxpayerType: "individual",
    taxType: "income_1040",
    amountOwed: "",
    returnsNeeded: [],
    notices: [],
    priorHelp: "no",
    priorHelpDetails: "",
    incomeSource: "W-2",
    monthlyIncome: "",
    consent: true,
  });

  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  const bg = { base: "white", _dark: "gray.800" };
  const border = { base: "gray.200", _dark: "whiteAlpha.300" };
  const sub = { base: "gray.600", _dark: "gray.300" };

  function setField(name, value) {
    setForm((f) => ({ ...f, [name]: value }));
  }

  function toggleInArray(name, value) {
    setForm((f) => {
      const arr = new Set(f[name] || []);
      arr.has(value) ? arr.delete(value) : arr.add(value);
      return { ...f, [name]: Array.from(arr) };
    });
  }
  function setArray(name, checked, value) {
    setForm((f) => {
      const next = new Set(f[name] || []);
      if (checked) next.add(value);
      else next.delete(value);
      return { ...f, [name]: Array.from(next) };
    });
  }

  function buildPayload(values) {
    const taxTypeLabel =
      (TAX_TYPES.find((t) => t.value === values.taxType) || {}).label ||
      values.taxType;

    const issues = [];
    (values.notices || []).forEach((n) => {
      if (!/None/i.test(n)) issues.push(n);
    });
    (values.returnsNeeded || []).forEach((r) => {
      if (!/Not sure/i.test(r)) issues.push(`Unfiled: ${r}`);
    });

    const notesParts = [];
    if (values.priorHelp === "yes") {
      notesParts.push(
        `Prior help attempted: ${values.priorHelpDetails || "Yes"}`
      );
    }
    if (values.incomeSource)
      notesParts.push(`Income source: ${values.incomeSource}`);
    if (values.monthlyIncome)
      notesParts.push(`Monthly income: $${values.monthlyIncome}`);
    if (values.returnsNeeded.includes("Not sure"))
      notesParts.push("Returns to file: Not sure");
    if (values.notices.includes("None / Not sure"))
      notesParts.push("Notices: None / Not sure");

    return {
      adminEmail,
      companyKey,
      fullName: values.fullName,
      email: values.email,
      phone: values.phone,
      state: values.state,
      taxpayerType: values.taxpayerType,
      taxType: taxTypeLabel,
      amountOwed: values.amountOwed,
      yearsUnfiled: values.returnsNeeded.join(", "),
      issues,
      notes: notesParts.join(" | "),
      source,
    };
  }

  async function onSubmit(e) {
    e.preventDefault();
    setMsg({ type: "", text: "" });

    if (!form.fullName || !form.email || !form.phone) {
      setMsg({ type: "error", text: "Please fill name, email, and phone." });
      return;
    }

    try {
      setSubmitting(true);
      const payload = buildPayload(form);
      // 🔌 Use ProspectContext instead of fetch/axios here
      await createProspect(payload);

      setMsg({ type: "success", text: "Thanks! We’re routing you now." });
      setForm((f) => ({
        ...f,
        fullName: "",
        email: "",
        phone: "",
        priorHelpDetails: "",
        monthlyIncome: "",
      }));
      if (typeof onSuccess === "function") onSuccess(payload);
    } catch (err) {
      // If your context function doesn’t throw on error, this catch may never run.
      setMsg({ type: "error", text: err?.message || "Something went wrong." });
    } finally {
      setSubmitting(false);
      alert(
        "Thank you for contacting us. Someone will reach out to you shortly."
      );
    }
  }

  return (
    <Box
      as="form"
      onSubmit={onSubmit}
      borderWidth="1px"
      borderColor={border}
      borderRadius="lg"
      bg={bg}
      p={{ base: 6, md: 8 }}
      w="full"
    >
      <Stack spacing={{ base: 5, md: 6 }}>
        <Heading size="md">Tell us about your tax issue</Heading>

        {msg.text ? (
          <Text
            fontSize="sm"
            color={msg.type === "error" ? "red.500" : "green.600"}
          >
            {msg.text}
          </Text>
        ) : null}

        {/* Contact */}
        <HStack spacing={{ base: 4, md: 5 }} align="stretch">
          <Box flex="1">
            <Text fontSize="sm" mb={1}>
              Full Name
            </Text>
            <Input
              size="lg"
              placeholder="Jane Smith"
              value={form.fullName}
              onChange={(e) => setField("fullName", e.target.value)}
              required
            />
          </Box>
          <Box flex="1">
            <Text fontSize="sm" mb={1}>
              Email
            </Text>
            <Input
              size="lg"
              type="email"
              placeholder="jane@example.com"
              value={form.email}
              onChange={(e) => setField("email", e.target.value)}
              required
            />
          </Box>
        </HStack>

        <HStack spacing={{ base: 4, md: 5 }} align="stretch">
          <Box flex="1">
            <Text fontSize="sm" mb={1}>
              Phone
            </Text>
            <Input
              size="lg"
              placeholder="(555) 201-0000"
              value={form.phone}
              onChange={(e) => setField("phone", e.target.value)}
              required
            />
          </Box>
          <Box w={{ base: "160px", md: "180px" }}>
            <Text fontSize="sm" mb={1}>
              State
            </Text>
            <Input
              size="lg"
              placeholder="CA"
              value={form.state}
              onChange={(e) => setField("state", e.target.value)}
            />
          </Box>
        </HStack>

        <Separator />

        {/* Basics */}
        <HStack spacing={{ base: 4, md: 5 }} align="stretch">
          <Box flex="1">
            <Text fontSize="sm" mb={1}>
              I am a…
            </Text>
            <NativeSelect.Root size="lg" width="100%">
              <NativeSelect.Field
                value={form.taxpayerType}
                onChange={(e) => setField("taxpayerType", e.target.value)}
              >
                <option value="individual">Individual</option>
                <option value="business">Business</option>
                <option value="both">Both</option>
                <option value="not_sure">Not sure</option>
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Box>

          <Box flex="1">
            <Text fontSize="sm" mb={1}>
              Primary Tax Type
            </Text>
            <NativeSelect.Root size="lg" width="100%">
              <NativeSelect.Field
                value={form.taxType}
                onChange={(e) => setField("taxType", e.target.value)}
              >
                {TAX_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Box>

          <Box flex="1">
            <Text fontSize="sm" mb={1}>
              Estimated Amount Owed
            </Text>
            <NativeSelect.Root size="lg" width="100%">
              <NativeSelect.Field
                value={form.amountOwed}
                onChange={(e) => setField("amountOwed", e.target.value)}
              >
                <option value="">Select…</option>
                {AMOUNT_OWED.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Box>
        </HStack>

        {/* Returns needed */}
        <Box>
          <Text fontSize="sm" mb={2}>
            Returns You Need to File
          </Text>
          <HStack spacing={3} wrap="wrap">
            {RETURNS_NEEDED.map((r) => (
              <Checkbox.Root
                key={r}
                checked={form.returnsNeeded.includes(r)}
                onCheckedChange={(e) => setArray("returnsNeeded", e.checked, r)}
              >
                <Checkbox.Control>
                  <Checkbox.Indicator />
                </Checkbox.Control>
                <Checkbox.Label>{r}</Checkbox.Label>
                <Checkbox.HiddenInput />
              </Checkbox.Root>
            ))}
          </HStack>
          <Text color={sub} fontSize="xs" mt={2}>
            Select all that apply. “Not sure” is okay.
          </Text>
        </Box>

        {/* Notices */}
        <Box>
          <Text fontSize="sm" mb={2}>
            IRS / State Notices Received
          </Text>
          <HStack spacing={3} wrap="wrap">
            {NOTICES.map((n) => (
              <Checkbox.Root
                key={n}
                checked={form.notices.includes(n)}
                onCheckedChange={(e) => setArray("notices", e.checked, n)}
              >
                <Checkbox.Control>
                  <Checkbox.Indicator />
                </Checkbox.Control>
                <Checkbox.Label>{n}</Checkbox.Label>
                <Checkbox.HiddenInput />
              </Checkbox.Root>
            ))}
          </HStack>
          <Text color={sub} fontSize="xs" mt={2}>
            If you selected CP504 or LT11/1058, there could be levy risk.
          </Text>
        </Box>

        {/* Prior help + details */}
        <HStack spacing={{ base: 4, md: 5 }} align="stretch">
          <Box flex="1">
            <Text fontSize="sm" mb={1}>
              Have you tried to get help?
            </Text>
            <NativeSelect.Root size="lg" width="100%">
              <NativeSelect.Field
                value={form.priorHelp}
                onChange={(e) => setField("priorHelp", e.target.value)}
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Box>
          <Box flex="2">
            <Text fontSize="sm" mb={1}>
              If yes, what did you try?
            </Text>
            <Input
              size="lg"
              placeholder="e.g., Spoke with CPA, prior firm, tried setting up plan…"
              value={form.priorHelpDetails}
              onChange={(e) => setField("priorHelpDetails", e.target.value)}
            />
          </Box>
        </HStack>

        {/* Income */}
        <HStack spacing={{ base: 4, md: 5 }} align="stretch">
          <Box flex="1">
            <Text fontSize="sm" mb={1}>
              How do you get your income?
            </Text>
            <NativeSelect.Root size="lg" width="100%">
              <NativeSelect.Field
                value={form.incomeSource}
                onChange={(e) => setField("incomeSource", e.target.value)}
              >
                {INCOME_SOURCES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Box>
          <Box flex="1">
            <Text fontSize="sm" mb={1}>
              Approx. Monthly Income
            </Text>
            <Input
              size="lg"
              type="number"
              min={0}
              placeholder="3000"
              value={form.monthlyIncome}
              onChange={(e) => setField("monthlyIncome", e.target.value)}
            />
          </Box>
        </HStack>

        {/* Consent */}
        <Box>
          <ConsentNotice />
        </Box>

        <Button
          type="submit"
          colorScheme="blue"
          size="lg"
          isLoading={submitting}
        >
          Check My Options
        </Button>
      </Stack>
    </Box>
  );
}
